import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import CueStyleBox from 'components/common/closedCaptions/CueStyleBox';
import { usePlayerCaptions } from 'components/providers/PlayerStateContext';
import { Cue } from 'app-types';
import { VideoCtvRedux, ClosedCaptionStyleProps } from 'store-types';

import styles from './ClosedCaptionsOverlay.module.scss';

const MAX_LINE_COUNT = 30;

// W3C documentation:
// https://www.w3.org/TR/webvtt1/

interface ClosedCaptionsOverlayProps {
  className?: string | null;
}

const ClosedCaptionsOverlay = ({
  className = null,
}: ClosedCaptionsOverlayProps) => {
  const styleSettings: ClosedCaptionStyleProps = {
    textSize: useSelector((state: VideoCtvRedux) => state.closedCaptionSettings.textSize),
    textColor: useSelector((state: VideoCtvRedux) => state.closedCaptionSettings.textColor),
    textType: useSelector((state: VideoCtvRedux) => state.closedCaptionSettings.textType),
    textOpacity: useSelector((state: VideoCtvRedux) => state.closedCaptionSettings.textOpacity),
    textShadow: useSelector((state: VideoCtvRedux) => state.closedCaptionSettings.textShadow),
    textShadowColor: useSelector(
      (state: VideoCtvRedux) => state.closedCaptionSettings.textShadowColor,
    ),
    backgroundColor: useSelector(
      (state: VideoCtvRedux) => state.closedCaptionSettings.backgroundColor,
    ),
    backgroundOpacity: useSelector(
      (state: VideoCtvRedux) => state.closedCaptionSettings.backgroundOpacity,
    ),
    windowColor: useSelector((state: VideoCtvRedux) => state.closedCaptionSettings.windowColor),
    windowOpacity: useSelector(
      (state: VideoCtvRedux) => state.closedCaptionSettings.windowOpacity,
    ),
  };

  const cues = usePlayerCaptions();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const html: React.ReactNode[] = [];

  // Font size calculation requires container height which requiring a re-render
  const [containerHeight, setContainerHeight] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const offsetHeight = containerRef.current?.offsetHeight;

    // To prevent re-render, only update containerHeight when the value is changed
    if (typeof offsetHeight === 'number' && containerHeight !== offsetHeight) {
      setContainerHeight(offsetHeight);
    }
  });

  const fontSize = Math.round(containerHeight / MAX_LINE_COUNT);

  // how to calculate line position:
  // https://www.w3.org/TR/webvtt1/#cues
  const calculateLinePos = (cue: Cue) => {
    if (
      typeof cue.line === 'number' &&
      (cue.snapToLines || (cue.line >= 0 && cue.line <= 100))
    ) {
      return cue.line;
    }
    if (!cue.snapToLines) return 100;
    if (
      !cue.track ||
      !cue.track.textTrackList ||
      !cue.track.textTrackList.mediaElement
    ) {
      return -1;
    }
    const { track } = cue;
    const trackList = track.textTrackList ?? [];
    let n = 0;
    for (let i = 0; i < trackList.length && trackList[i] !== track; i += 1) {
      if (trackList[i].mode === 'showing') {
        n += 1;
      }
    }
    n += 1;
    return -n;
  };

  let lastLinePosition = -1;
  const cueBoxPositions: Record<number, JSX.Element> = {};
  Object.keys(cues ?? {}).forEach((key) => {
    const cue = cues![key];
    const linePos = calculateLinePos(cue);
    const { vertical, positionAlign, position } = cue;
    let { size } = cue;
    let textPos;
    switch (positionAlign) {
      case 'center':
        textPos = (position as number) - (size as number) / 2;
        break;
      case 'end':
        textPos = (position as number) - (size as number);
        break;
      case 'start':
      default:
        textPos = position;
    }
    if (!(typeof textPos === 'number')) {
      textPos = 0;
    }
    size = textPos + (size as number) > 100 ? 100 - textPos : size;
    const line = linePos < 0 ? linePos + MAX_LINE_COUNT : linePos;
    if (line > lastLinePosition) lastLinePosition = line;

    const style =
      vertical === ''
        ? {
            marginLeft: `${textPos}%`,
            width: `${size}%`,
          }
        : {
            topLeft: `${textPos}%`,
            height: `${size}%`,
          };
    const cueBox = (
      <CueStyleBox
        className={styles.textLine}
        cue={cue as unknown as Cue}
        key={`cuebox-${cue.text}`}
        settings={styleSettings}
        style={style}
      />
    );

    cueBoxPositions[line] = cueBox;
  });

  const spacerHeight = fontSize + containerHeight * 0.02; // font size + top & bottom margin
  let spacerCount = 0;
  for (let i = 0; i <= lastLinePosition; i += 1) {
    const cueBox = cueBoxPositions[i];
    if (cueBox) {
      spacerCount &&
        html.push(
          <div
            style={{ height: spacerHeight * spacerCount }}
            key={`cuebox-spacer-${i}`}
          />,
        );
      html.push(cueBox);
      spacerCount = 0;
    } else spacerCount += 1;
  }

  return (
    <div
      id="closed-caption-overlay"
      className={classNames(styles.overlay, className)}
      data-testid="closed-caption-overlay"
      ref={containerRef}
      style={{ fontSize: `${fontSize}px` }}
    >
      {html}
    </div>
  );
};

export default React.memo(ClosedCaptionsOverlay);
